import { createFileRoute, notFound, redirect } from "@tanstack/react-router";
import { i18nLinks, i18nOgMeta } from "@/lib/seo";
import { getLocale } from "@/paraglide/runtime";
import {
  blogPostLocationQueryOptions,
  blogPostsQueryOptions,
  blogTopicQueryOptions,
} from "@/tools/blog/hooks/use-blog-data";
import { pageDeps, pageSearch } from "@/tools/blog/lib/page-search";
import { BlogTopic } from "@/tools/blog/pages/BlogTopic";

export const Route = createFileRoute("/blog/$tool/$topic/")({
  component: BlogTopicRoute,
  ssr: true,
  validateSearch: pageSearch,
  loaderDeps: pageDeps,
  loader: async ({ context, params, deps }) => {
    const locale = getLocale();
    const topic = await context.queryClient.ensureQueryData(
      blogTopicQueryOptions(params.tool, params.topic, locale),
    );

    // If $topic isn't a real topic, it may be an old flat post URL
    // /blog/<tool>/<slug> — 301-redirect to the canonical 3-segment URL.
    if (!topic) {
      const loc = await context.queryClient.ensureQueryData(
        blogPostLocationQueryOptions(params.topic, locale),
      );
      if (loc && loc.toolSlug === params.tool) {
        throw redirect({
          to: "/blog/$tool/$topic/$slug",
          params: {
            tool: loc.toolSlug,
            topic: loc.topicSlug,
            slug: loc.slug,
          },
          statusCode: 301,
        });
      }
      // Neither a topic nor a legacy post slug → real 404 (was a 200 shell).
      throw notFound();
    }

    const postsPage = await context.queryClient.ensureQueryData(
      blogPostsQueryOptions(locale, params.tool, params.topic, deps.page),
    );
    // Past the last page → 404 rather than an empty listing.
    if (deps.page > 1 && postsPage.items.length === 0) throw notFound();
    return { topic, total: postsPage?.total ?? 0 };
  },
  head: ({ loaderData, params }) => {
    const name = loaderData?.topic?.name ?? params.topic;
    const basePath = `/blog/${params.tool}/${params.topic}`;
    return {
      meta: [
        // Empty topic hubs are thin pages — noindex until posts exist.
        ...(loaderData && loaderData.total === 0
          ? [{ name: "robots", content: "noindex, follow" }]
          : []),
        { title: `${name} | Toolverse Blog` },
        ...(loaderData?.topic?.description
          ? [{ name: "description", content: loaderData.topic.description }]
          : []),
        ...i18nOgMeta(basePath),
      ],
      links: [...i18nLinks(basePath)],
    };
  },
});

function BlogTopicRoute() {
  const { tool, topic } = Route.useParams();
  const { page } = Route.useSearch();
  return <BlogTopic tool={tool} topic={topic} page={page ?? 1} />;
}
